import { notFound, redirect } from "@tanstack/react-router";
import { ApiError, backendFetch } from "@softmax/observatory-client/transport";
import type { Schemas } from "@softmax/observatory-client/types";
import { buildHref } from "@softmax/web-addresses";
import { getActualViewer } from "@/data/viewer";
import { getAuthSession } from "@/features/identity/session";
import {
  appendResourceSearch,
  resourcePath,
  type ResourceDestination,
  type ResourceInput,
  type ResourceSearch,
} from "./resource-search";
export type ResourceViewer = Schemas["WhoAmIResponse"] | null;
export function redirectTo(href: string): never {
  throw redirect({ href });
}
export async function readResource<T>({
  path,
  returnHref,
  signal,
}: {
  path: string;
  returnHref: string;
  signal?: AbortSignal | undefined;
}): Promise<{
  resource: T;
  viewer: ResourceViewer;
  generatedAt: number;
}> {
  const signInHref = buildHref({
    name: "signIn",
    search: new URLSearchParams({ redirect_url: returnHref }),
  });
  return getActualViewer({ signal })
    .then(async (viewer) => {
      const resource = await backendFetch<T>("/api/observatory", path, {
        credentials: viewer ? "same-origin" : "omit",
        headers: { "X-Use-Elevated-Privileges": "false" },
        signal: signal ?? null,
      });
      return { resource, viewer, generatedAt: Date.now() };
    })
    .catch((error: unknown) => {
      if (error instanceof ApiError) {
        if (error.status === 401) redirectTo(signInHref);

        if (error.status === 403 || error.status === 404) throw notFound();
      }

      throw error;
    });
}
export function canonicalizeResource({
  input,
  destination,
  canonical,
  search = input.search,
}: {
  readonly input: ResourceInput;
  readonly destination: ResourceDestination;
  readonly canonical: string;
  readonly search?: ResourceSearch;
}): void {
  if (
    canonical !==
    resourcePath({
      input,
      destination,
    })
  ) {
    redirectTo(
      appendResourceSearch({
        href: canonical,
        search,
      }),
    );
  }
}
export async function admitPublicDetail({
  destination,
  search,
  signal,
}: {
  destination:
    | {
        readonly name: "player";
        readonly playerId: string;
      }
    | {
        readonly name: "tournament";
        readonly tournamentId: string;
      }
    | {
        readonly name: "experienceRequest";
        readonly requestId: string;
      };
  search: ResourceSearch;
  signal?: AbortSignal | undefined;
}): Promise<void> {
  if (await getAuthSession({ signal })) return;

  redirectTo(
    buildHref({
      name: "signIn",
      search: new URLSearchParams({
        redirect_url: appendResourceSearch({
          href: buildHref(destination),
          search,
        }),
      }),
    }),
  );
}
